/**
 * Shared "most-recent-activity" helpers for ordering and labelling trips.
 *
 * A trip is "modified" by more than direct edits: sending a chat message bumps
 * `derived.lastMessageAt` (maintained server-side, seeded to `createdAt` on create), while trip edits
 * and working-memory syncs bump the top-level `updatedAt`. Neither field alone captures "when was this
 * trip last touched", so the activity moment is the *latest* of `derived.lastMessageAt`, `updatedAt`,
 * and `createdAt`. Living in the (DOM-free) client-core lets web (`client/src`) and mobile (`mobile/src`)
 * share one definition for both list ordering and the "Last updated …" card subtitle.
 */
import { toDate } from '#lib/date-utils.js';

/** The subset of trip fields that determine recency. All optional/`unknown` so plain fixtures work. */
export interface TripActivityFields {
  updatedAt?: unknown;
  createdAt?: unknown;
  derived?: { lastMessageAt?: unknown } | null;
}

/** The most-recent-activity moment: the latest of `derived.lastMessageAt`, `updatedAt`, `createdAt`. */
export function getTripActivityDate(trip: TripActivityFields): Date | undefined {
  let latest: Date | undefined;
  for (const candidate of [trip.derived?.lastMessageAt, trip.updatedAt, trip.createdAt]) {
    const date = toDate(candidate);
    if (date && (!latest || date.getTime() > latest.getTime())) latest = date;
  }
  return latest;
}

/** The most-recent-activity timestamp in ms, or `0` when the trip carries no usable dates. */
export function getTripActivityTimestamp(trip: TripActivityFields): number {
  return getTripActivityDate(trip)?.getTime() ?? 0;
}

/** Comparator ordering trips most-recently-active first (descending activity). */
export function compareTripsByActivityDesc(a: TripActivityFields, b: TripActivityFields): number {
  return getTripActivityTimestamp(b) - getTripActivityTimestamp(a);
}
